// Canonical source: html_iscreamtoolkit_new. Mirror with scripts/sync-ga-user-context.cjs.
// Analytics context only: decoding a JWT does not validate a login or its signature.
const CACHE_MS = 5 * 60 * 1000;
const RETRY_MS = 30 * 1000;
const BROKER_KEY = '__toolkitAnalyticsBrokerV1';
const CONTROLLER_KEY = '__toolkitAnalyticsControllerV1';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function readCookie(cookie, name) {
    const item = cookie.split(';').find(part => part.trim().startsWith(name + '='));
    if (!item) return '';
    try { return decodeURIComponent(item.trim().slice(name.length + 1)); }
    catch { return ''; }
}

export function readIdentity(cookie, now = Date.now()) {
    const token = readCookie(cookie, 'idToken');
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const bytes = Uint8Array.from(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')), c => c.charCodeAt(0));
        const payload = JSON.parse(new TextDecoder().decode(bytes));
        const uuid = payload.uuid || payload.user?.uuid;
        if (typeof uuid !== 'string' || !UUID.test(uuid)) return null;
        if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp) || payload.exp * 1000 <= now) return null;
        const tokenMid = payload.mId || payload.user?.mId || payload.principalName;
        const cookieMid = readCookie(cookie, 'mId');
        if (tokenMid && cookieMid && tokenMid !== cookieMid) return null;
        const mId = tokenMid || cookieMid;
        return {
            token, uuid, mId: typeof mId === 'string' ? mId : '',
            memDiv: payload.memDiv || payload.user?.memDiv,
            expires: payload.exp * 1000,
        };
    } catch { return null; }
}

export function getApiOrigins(hostname) {
    const stage = hostname === 'localhost' || hostname === '[::1]' ||
        /^(127\.|10\.|192\.168\.)/.test(hostname) || hostname === 'stagetoolkit.i-scream.co.kr';
    return {
        service: stage ? 'https://stageapi.i-scream.co.kr' : 'https://api.i-scream.co.kr',
        account: stage ? 'https://stageaccount-api.i-screammedia.com' : 'https://account-api.i-screammedia.com',
    };
}

export function mapUserType(memDiv) {
    if (memDiv === 'teachermember') return '초중고 교원 교직원';
    // The account owner confirmed there are only two membership types.
    if (typeof memDiv === 'string' && memDiv.trim() && !['undefined', 'null'].includes(memDiv)) return '일반회원';
    return undefined;
}

export function mapMemberProperties(member) {
    const properties = { user_detail_type: undefined, grade: undefined };
    if (!member || typeof member !== 'object' || Array.isArray(member)) return properties;
    // The taxonomy has one role only. Do not guess if the API reports both roles.
    if (member.memberChargeYn === 'Y' && member.memberDutyYn === 'Y') return properties;
    if (member.memberChargeYn === 'Y') {
        properties.user_detail_type = '교과전담';
        const grades = Array.isArray(member.chargeList) ? member.chargeList
            .map(item => item?.mChargeGrade)
            .filter(value => (typeof value === 'number' || typeof value === 'string') &&
                /^[1-6]$/.test(String(value)))
            .map(Number) : [];
        if (grades.length) properties.grade = [...new Set(grades)].sort((a, b) => a - b).join(',');
    } else if (member.memberDutyYn === 'Y') {
        properties.user_detail_type = '담임';
    }
    return properties;
}

async function requestJson(fetcher, url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
        const response = await fetcher(url, { ...options, signal: controller.signal, cache: 'no-store' });
        if (!response.ok) throw new Error('Analytics member request failed');
        return await response.json();
    } finally { clearTimeout(timeout); }
}

async function transformData(fetcher, service, operation, value, field) {
    const body = new FormData();
    body.append('pData', value);
    const result = await requestJson(fetcher, service + '/api/common/' + operation, { method: 'POST', body });
    if (typeof result[field] !== 'string' || !result[field]) throw new Error('Invalid analytics member response');
    return result[field];
}

export async function loadProperties(identity, origins, fetcher) {
    const properties = {
        user_id: identity.uuid, user_type: mapUserType(identity.memDiv),
        user_detail_type: undefined, grade: undefined,
    };
    let failed = false;
    const accountTask = properties.user_type ? Promise.resolve() : (async () => {
        const account = await requestJson(fetcher, origins.account + '/member/information_nickname?site=iscream', {
            headers: { Authorization: 'Bearer ' + identity.token },
        });
        if (account.mId && identity.mId && account.mId !== identity.mId) throw new Error('Account mismatch');
        properties.user_type = mapUserType(account.memDiv);
        if (!properties.user_type) failed = true;
    })();
    const memberTask = !identity.mId ? Promise.resolve().then(() => { failed = true; }) : (async () => {
        const encoded = await transformData(fetcher, origins.service, 'selectDataEncode', identity.mId, 'encodeData');
        const result = await requestJson(fetcher, origins.service + '/api/member/selectMemberInfo/' + encodeURIComponent(encoded));
        if (String(result.resultCode) === '-1') return;
        if (String(result.resultCode) !== '1' || typeof result.memberInfo !== 'string' || !result.memberInfo) {
            throw new Error('Invalid analytics member response');
        }
        const decoded = await transformData(fetcher, origins.service, 'selectDataDecode', result.memberInfo, 'decodeData');
        const member = JSON.parse(decoded);
        if (member?.memberId && member.memberId !== identity.mId) throw new Error('Member mismatch');
        Object.assign(properties, mapMemberProperties(member));
    })();
    const results = await Promise.allSettled([accountTask, memberTask]);
    if (results.some(result => result.status === 'rejected')) failed = true;
    return { properties, status: failed ? 'partial' : 'ready' };
}

function createBroker(win) {
    let cached;
    return {
        version: 1,
        get(identity) {
            const key = identity.token + '\n' + identity.mId;
            if (cached?.key === key && cached.until > Date.now()) return cached.promise;
            const entry = { key, until: Infinity };
            entry.promise = loadProperties(identity, getApiOrigins(win.location.hostname), win.fetch.bind(win))
                .then(result => {
                    entry.until = Math.min(identity.expires, Date.now() + (result.status === 'ready' ? CACHE_MS : RETRY_MS));
                    return result;
                });
            cached = entry;
            return entry.promise;
        },
        clear() { cached = undefined; },
    };
}

function getBroker(win) {
    // Share only within the same origin. A standalone/cross-origin tool works independently.
    let owner = win;
    for (const candidate of [win.top, win.opener]) {
        try {
            if (candidate && !candidate.closed && candidate.location.origin === win.location.origin) {
                owner = candidate;
                if (owner !== win) break;
            }
        } catch { /* Cross-origin parent/opener is intentionally not used. */ }
    }
    try {
        if (owner[BROKER_KEY]?.version !== 1) owner[BROKER_KEY] = createBroker(owner);
        return owner[BROKER_KEY];
    } catch { return createBroker(win); }
}

export function startUserContext(win, { enabled = true } = {}) {
    // This guard precedes every cookie read, API call and dataLayer write.
    if (!enabled) return undefined;
    if (win[CONTROLLER_KEY]) return win[CONTROLLER_KEY];
    const broker = getBroker(win);
    let activeKey;
    let generation = 0;
    let nextRefresh = 0;
    let pending = false;
    let stopped = false;
    let lastMessage;
    let lastUserId;
    const publish = (properties, status) => {
        const message = {
            event: 'toolkit_user_context',
            user_id: properties.user_id ?? null,
            user_type: properties.user_type,
            user_detail_type: properties.user_detail_type,
            grade: properties.grade,
            toolkit_user_context_status: status,
        };
        const signature = JSON.stringify(message);
        if (lastMessage === signature) return;
        lastMessage = signature;
        win.dataLayer = win.dataLayer || [];
        if (lastUserId !== message.user_id) {
            lastUserId = message.user_id;
            if (typeof win.gtag === 'function') win.gtag('set', { user_id: message.user_id });
        }
        win.dataLayer.push(message);
    };
    const refresh = () => {
        if (stopped) return;
        const identity = readIdentity(win.document.cookie);
        const key = identity ? identity.token + '\n' + identity.mId : '';
        if (key !== activeKey) {
            activeKey = key;
            generation++;
            pending = false;
            nextRefresh = 0;
            if (!identity) broker.clear();
            publish(identity ? { user_id: identity.uuid, user_type: mapUserType(identity.memDiv) } : {},
                identity ? 'loading' : 'anonymous');
        }
        if (!identity || pending || Date.now() < nextRefresh) return;
        pending = true;
        const requestGeneration = generation;
        broker.get(identity).then(result => {
            if (stopped || requestGeneration !== generation) return;
            const current = readIdentity(win.document.cookie);
            if (!current || current.token !== identity.token || current.mId !== identity.mId) {
                pending = false;
                refresh();
                return;
            }
            pending = false;
            nextRefresh = Date.now() + (result.status === 'ready' ? CACHE_MS : RETRY_MS);
            publish(result.properties, result.status);
        }).catch(() => {
            if (stopped || requestGeneration !== generation) return;
            pending = false;
            nextRefresh = Date.now() + RETRY_MS;
            // Never log a token, API response, member ID or UUID.
        });
    };
    const events = ['focus', 'pageshow', 'toolkit:auth-changed'];
    events.forEach(name => win.addEventListener(name, refresh));
    const visible = () => { if (win.document.visibilityState !== 'hidden') refresh(); };
    win.document.addEventListener('visibilitychange', visible);
    // Check before user interactions; the timer also handles expiry without interaction.
    win.document.addEventListener('pointerdown', refresh, true);
    win.document.addEventListener('keydown', refresh, true);
    const interval = win.setInterval(refresh, 1000);
    const controller = {
        refresh,
        stop() {
            stopped = true;
            generation++;
            win.clearInterval(interval);
            events.forEach(name => win.removeEventListener(name, refresh));
            win.document.removeEventListener('visibilitychange', visible);
            win.document.removeEventListener('pointerdown', refresh, true);
            win.document.removeEventListener('keydown', refresh, true);
            delete win[CONTROLLER_KEY];
        },
    };
    win[CONTROLLER_KEY] = controller;
    refresh();
    return controller;
}
